import { useMemo } from 'react'

import type { RemindersEditorReminderAPIEntity } from '@surglogs-app/network'

import {
  RemindersEditorDefaultOptions,
  REMINDERS_EDITOR_OPTION_CUSTOM,
} from '../constants'
import { useTranslation } from '../services'
import {
  getOccupiedReminders,
  getReminderOptionText,
  getSortedReminders,
} from '../services/RemindersService'
import type { RemindersEditorReminderValue } from '../types'

export const useRemindersSelectOptions = ({
  clinicReminders,
  selectedReminders,
  allowCreateReminder = true,
}: {
  clinicReminders?: RemindersEditorReminderAPIEntity[]
  selectedReminders: RemindersEditorReminderValue[]
  allowCreateReminder?: boolean
}) => {
  const { t } = useTranslation()

  const occupiedReminders = useMemo(
    () => getOccupiedReminders(selectedReminders),
    [selectedReminders],
  )

  const allOptions = useMemo(() => {
    const combinedReminders = [
      ...Object.values(RemindersEditorDefaultOptions),
      ...(clinicReminders ?? []),
    ]
    const uniqueReminders = Array.from(
      new Map(
        combinedReminders.map((reminder) => [
          getReminderOptionText(reminder),
          reminder,
        ]),
      ).values(),
    )

    return getSortedReminders(uniqueReminders)
  }, [clinicReminders])

  return [
    ...allOptions.map((reminder) => ({
      id: getReminderOptionText(reminder),
      disabled: !!occupiedReminders[getReminderOptionText(reminder)],
      text: reminder.unit
        ? t(`frequency.${reminder.unit}`, { count: reminder.amount ?? 0 })
        : t('frequency.custom'),
      default: 'id' in reminder ? !!reminder.id : false,
    })),
    ...(allowCreateReminder
      ? [
          {
            id: REMINDERS_EDITOR_OPTION_CUSTOM,
            disabled: false,
            text: t(`frequency.custom`),
          },
        ]
      : []),
  ]
}
